/**
 * post-auth-redirect
 *
 * Resolves where to send a user after a successful sign-in or sign-up.
 *
 * When a satellite app (e.g. business-web) redirects an unauthenticated user
 * to this primary's `/login`, it appends a `redirect_url` (or `redirectUrl` /
 * `redirect_uri`) query param that points back to the satellite. After the
 * user completes sign-in on the primary, we must send them to that URL so
 * Clerk's cross-origin satellite handshake can finalize on the satellite.
 *
 * Without this, users who arrive at citizen's `/login` from business-web
 * end up stuck on citizen's `/dashboard` and never return to business-web.
 *
 * Security: the URL is validated against an allow-list of Tonnie domains
 * (both `*.tonnie.ai` subdomains and `localhost:*`) so a crafted link
 * can't redirect users to a phishing site after sign-in.
 */

/** Regex for hosts we consider safe to redirect into after sign-in. */
const ALLOWED_HOST_PATTERNS: ReadonlyArray<RegExp> = [
  /^([a-z0-9-]+\.)*tonnie\.ai$/i,
  /^localhost(?::\d+)?$/i,
  /^127\.0\.0\.1(?::\d+)?$/,
];

function isAllowedHost(host: string): boolean {
  return ALLOWED_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

/**
 * Read the intended post-auth redirect from the current URL.
 *
 * Accepts any of the common query-param names that Clerk or other
 * redirectors might use (`redirect_url`, `redirectUrl`, `redirect_uri`).
 *
 * Returns a trusted absolute URL string, a same-origin path, or `null` if
 * no redirect is requested or the requested target is not allow-listed.
 */
export function readPostAuthRedirect(search: string = window.location.search): string | null {
  const params = new URLSearchParams(search);
  const raw =
    params.get('redirect_url') || params.get('redirectUrl') || params.get('redirect_uri') || null;

  if (!raw) return null;

  // Same-origin absolute path (e.g. "/dashboard?x=1") is always safe.
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    return raw;
  }

  // Any other candidate must parse as a URL with an allow-listed host.
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!isAllowedHost(url.host)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Perform the post-auth navigation. Falls back to `fallback` (usually
 * `/dashboard`) when no valid redirect is present.
 *
 * Uses a hard `window.location.assign` for cross-origin targets so the
 * browser actually leaves this origin and Clerk's satellite handshake
 * finalises; uses the provided same-origin navigator (typically wouter's
 * `setLocation`) for same-origin paths.
 */
export function navigateAfterAuth(
  fallback: string,
  sameOriginNavigate: (path: string) => void
): void {
  const target = readPostAuthRedirect();

  if (!target) {
    sameOriginNavigate(fallback);
    return;
  }

  // Same-origin path → router navigation (preserves SPA state)
  if (target.startsWith('/')) {
    sameOriginNavigate(target);
    return;
  }

  // Cross-origin (e.g. satellite) → hard navigation so the target origin's
  // Clerk instance picks up the handshake cookie.
  window.location.assign(target);
}
